import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  DragStartEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import ApplicationForm from "../components/ApplicationForm";
import RejectionPopover from "../components/RejectionPopover";
import TableView from "../components/crm/TableView";
import { Chip } from "@mui/material";
import type {
  ViewMode,
  PipelineApplication,
  SortableApplicationCardProps,
  DragOverlayCardProps,
  MoveApplicationStatusPayload,
  KanbanColumn,
  StatusColumnProps,
} from "../types/pipeline.types";
import {
  APPLICATION_KANBAN_COLUMNS,
  APPLICATION_STATUS_CONFIG,
  APPLICATION_VALID_TRANSITIONS,
} from "../components/StatusControl/configs/applicationConfig";
import "./PipelineKanban.css";

// Convert snake_case DB values to camelCase translation keys
const toCamelCase = (str: string): string =>
  str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

// Helper to translate status values with fallback to formatted raw value
const translateStatus = (
  status: string | undefined,
  prefix: string,
  t: (key: string) => string,
): string => {
  if (!status) return "";
  // Try camelCase key first (DB stores snake_case, translations use camelCase)
  const camelKey = `${prefix}.${toCamelCase(status)}`;
  const camelTranslated = t(camelKey);
  if (camelTranslated !== camelKey) return camelTranslated;
  // Fall back to raw key (for values that are already camelCase or single-word)
  const rawKey = `${prefix}.${status}`;
  const rawTranslated = t(rawKey);
  if (rawTranslated !== rawKey) return rawTranslated;
  // Last resort: format the raw value
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ");
};

// Status-based Column Component (for application Kanban view)
const StatusColumn: React.FC<StatusColumnProps & { highlighted?: boolean }> = ({
  column,
  children,
  itemCount,
  highlighted,
}) => {
  const { t } = useTranslation(["recruiting"]);
  const droppableId = `status-col-${column.key}`;
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
  });

  // Use color from the primary status in this column
  const primaryStatus = column.statuses[0];
  const statusConfig = APPLICATION_STATUS_CONFIG[primaryStatus];
  const headerColor = statusConfig?.color || "#6b7280";

  return (
    <div
      id={`pipeline-col-${column.key}`}
      ref={setNodeRef}
      className={`pipeline-column ${isOver ? "dropping" : ""} ${highlighted ? "pipeline-column--highlighted" : ""}`}
    >
      <div className="column-header" style={{ backgroundColor: headerColor }}>
        <h3 title={column.label}>{column.label}</h3>
        <span className="application-count">{itemCount}</span>
      </div>

      <div className="column-content">
        {itemCount > 0 ? (
          children
        ) : (
          <div className="pipeline-empty-column">
            <p>{t("applications.emptyColumn")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Sortable Application Card Component
// Interview sub-statuses for badge dropdown
const INTERVIEW_STATUSES: import("../types/shared.types").ApplicationStatus[] =
  ["interview_waiting", "interview_scheduled"];

const SortableApplicationCard: React.FC<SortableApplicationCardProps> = ({
  application,
  onViewCandidate,
  onViewJob,
  onTechCheck,
  onCardClick,
  onStatusChange,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: application.id.toString() });

  // Close dropdown on drag start
  useEffect(() => {
    if (isDragging) setShowStatusDropdown(false);
  }, [isDragging]);

  // Close dropdown on click outside
  useEffect(() => {
    if (!showStatusDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showStatusDropdown]);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleViewCandidate = (e: React.MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    if (onViewCandidate && application.candidate_id) {
      onViewCandidate(application.candidate_id);
    }
  };

  const handleViewJob = (e: React.MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    if (onViewJob && application.job_id) {
      onViewJob(application.job_id);
    }
  };

  const handleTechCheck = (e: React.MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    if (onTechCheck && application.candidate_id) {
      onTechCheck(application.candidate_id);
    }
  };

  const handleCardClick = (): void => {
    if (!isDragging && onCardClick) {
      onCardClick(application.id);
    }
  };

  const candidateName =
    `${application.candidate_first_name || application.first_name || t("common:unknown")} ${application.candidate_last_name || application.last_name || ""}`.trim();
  return (
    <div className="application-card-wrapper">
      {/* Draggable card content */}
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        role="button"
        tabIndex={0}
        className={`pipeline-card ${isDragging ? "dragging" : ""}`}
        onClick={handleCardClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleCardClick();
          }
        }}
      >
        {/* Header: Name + Status Badge */}
        <div className="pipeline-card-header">
          <span className="pipeline-card-name" title={candidateName}>
            {candidateName}
          </span>
          {INTERVIEW_STATUSES.includes(
            application.status as import("../types/shared.types").ApplicationStatus,
          ) && onStatusChange ? (
            <div className="pipeline-badge-dropdown-wrapper" ref={dropdownRef}>
              <button
                type="button"
                className={`pipeline-status-badge status-${application.status} pipeline-badge-clickable`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowStatusDropdown((prev) => !prev);
                }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                {translateStatus(
                  application.status,
                  "applications.form.status",
                  t,
                )}
                <span className="badge-dropdown-arrow">&#9662;</span>
              </button>
              {showStatusDropdown && (
                <div className="pipeline-badge-dropdown">
                  {INTERVIEW_STATUSES.filter(
                    (s) => s !== application.status,
                  ).map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onStatusChange(application.id, status);
                        setShowStatusDropdown(false);
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      {translateStatus(status, "applications.form.status", t)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <span
              className={`pipeline-status-badge status-${application.status}`}
            >
              {translateStatus(
                application.status,
                "applications.form.status",
                t,
              )}
            </span>
          )}
        </div>

        {/* Job info */}
        <div className="pipeline-card-body">
          <span className="pipeline-card-job">{application.job_title}</span>
          <span className="pipeline-card-location">
            {application.job_location}
          </span>
        </div>

        {/* Meta: Rate (conditional) + Applied */}
        <div className="pipeline-card-meta">
          {application.expected_rate != null && (
            <div className="meta-row">
              <span className="label">{t("applications.rate")}:</span>
              <span className="value">
                ${application.expected_rate}/{t("applications.day")}
              </span>
            </div>
          )}
          {application.created_at && (
            <div className="meta-row">
              <span className="label">{t("applications.applied")}:</span>
              <span className="value">
                {new Date(application.created_at).toLocaleDateString()}
              </span>
            </div>
          )}
          {application.ai_match_score != null && (
            <div className="meta-row">
              <span className="label">AI Match:</span>
              <span className="value">{application.ai_match_score}%</span>
            </div>
          )}
          {application.status_changed_at && (
            <div className="meta-row">
              <span className="label">In status:</span>
              <span className="value">
                {Math.max(
                  0,
                  Math.floor(
                    (Date.now() -
                      new Date(application.status_changed_at).getTime()) /
                      (1000 * 60 * 60 * 24),
                  ),
                )}
                d
              </span>
            </div>
          )}
        </div>

        {/* Tags */}
        {application.candidate_tags &&
          application.candidate_tags.length > 0 && (
            <div className="pipeline-card-tags">
              {application.candidate_tags.slice(0, 3).map((tag) => (
                <span key={`app-${application.id}-${tag}`} className="tag">
                  {tag}
                </span>
              ))}
              {application.candidate_tags.length > 3 && (
                <span className="tag-more">
                  +{application.candidate_tags.length - 3}
                </span>
              )}
            </div>
          )}
      </div>

      {/* Actions - hover reveal, outside drag ref */}
      <div className="pipeline-card-actions">
        <button
          className="pipeline-action-btn"
          onClick={handleViewCandidate}
          title={t("applications.viewCandidate")}
        >
          {t("applications.viewCandidate")}
        </button>
        <button
          className="pipeline-action-btn"
          onClick={handleViewJob}
          title={t("applications.viewJob")}
        >
          {t("applications.viewJob")}
        </button>
        {application.status === "approved" && (
          <button
            className="pipeline-action-btn primary"
            onClick={handleTechCheck}
            title={t("applications.techCheck")}
          >
            {t("applications.techCheck")}
          </button>
        )}
      </div>
    </div>
  );
};

// Drag Overlay Component
const DragOverlayCard: React.FC<DragOverlayCardProps> = ({ application }) => {
  const { t } = useTranslation(["recruiting", "common"]);

  if (!application) {
    return null;
  }

  const overlayName =
    `${application.candidate_first_name || application.first_name || t("common:unknown")} ${application.candidate_last_name || application.last_name || ""}`.trim();
  return (
    <div className="pipeline-card dragging-overlay">
      <div className="pipeline-card-header">
        <span className="pipeline-card-name">{overlayName}</span>
        {application.status && (
          <span
            className={`pipeline-status-badge status-${application.status}`}
          >
            {translateStatus(application.status, "applications.form.status", t)}
          </span>
        )}
      </div>
      <div className="pipeline-card-body">
        <span className="pipeline-card-job">{application.job_title}</span>
        <span className="pipeline-card-location">
          {application.job_location}
        </span>
      </div>
      {application.expected_rate != null && (
        <div className="pipeline-card-meta">
          <div className="meta-row">
            <span className="label">{t("applications.rate")}:</span>
            <span className="value">
              ${application.expected_rate}/{t("applications.day")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const PipelineKanban: React.FC = () => {
  const { t } = useTranslation(["recruiting", "common"]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showCreateApplicationModal, setShowCreateApplicationModal] =
    useState<boolean>(false);
  const [pendingRejection, setPendingRejection] = useState<{
    applicationId: number;
    columnLabel: string;
  } | null>(null);
  const [pendingInterviewChoice, setPendingInterviewChoice] = useState<{
    applicationId: number;
  } | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("kanban");
  const [highlightedColumn, setHighlightedColumn] = useState<string | null>(
    null,
  );
  const [showCompleted, setShowCompleted] = useState<boolean>(() => {
    try {
      return localStorage.getItem("pipeline-show-completed") === "true";
    } catch {
      return false;
    }
  });

  // Read ?status= param from URL (e.g. from dashboard funnel click) and scroll to that column
  useEffect(() => {
    const statusParam = searchParams.get("status");
    if (statusParam) {
      // Find the kanban column that contains this status
      const column = APPLICATION_KANBAN_COLUMNS.find(
        (col) => col.key === statusParam || col.statuses.includes(statusParam),
      );
      if (column) {
        setHighlightedColumn(column.key);
        // Scroll to column after render
        setTimeout(() => {
          const el = document.getElementById(`pipeline-col-${column.key}`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", inline: "center" });
          }
          // Remove highlight after 3 seconds
          setTimeout(() => setHighlightedColumn(null), 3000);
        }, 300);
      }
    }
  }, [searchParams]);

  const toggleShowCompleted = () => {
    setShowCompleted((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("pipeline-show-completed", String(next));
      } catch {
        // localStorage unavailable
      }
      return next;
    });
  };

  // Terminal statuses that are hidden by default
  const TERMINAL_COLUMN_KEYS = new Set(["success", "rejected"]);

  // Fetch applications
  const {
    data: applicationsData,
    isLoading: applicationsLoading,
    error: applicationsError,
  } = useQuery("applications", () => api.recruiting.getApplications(), {
    staleTime: 30 * 1000, // 30 seconds
  });

  // Move application status mutation
  const moveApplicationMutation = useMutation(
    async ({
      applicationId,
      status,
      note,
      override_justification,
      rejection_reason,
      rejection_reason_note,
    }: MoveApplicationStatusPayload) => {
      return await api.recruiting.updateApplicationStatus(
        applicationId,
        status,
        {
          note,
          override_justification,
          rejection_reason,
          rejection_reason_note,
        },
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("applications");
      },
      onError: (error: Error) => {
        console.error("Error moving application:", error);
        alert("Failed to move application: " + error.message);
      },
    },
  );

  const applications = useMemo<PipelineApplication[]>(
    () => Array.isArray(applicationsData) ? applicationsData : [],
    [applicationsData],
  );

  // Compute terminal status counts for the toggle button
  const terminalCounts = useMemo(() => {
    let successCount = 0;
    let rejectedCount = 0;
    for (const app of applications) {
      if (app.status === "success") successCount++;
      else if (app.status === "rejected") rejectedCount++;
    }
    return { success: successCount, rejected: rejectedCount };
  }, [applications]);

  // Filter columns based on showCompleted toggle
  const visibleColumns = useMemo(
    () =>
      showCompleted
        ? APPLICATION_KANBAN_COLUMNS
        : APPLICATION_KANBAN_COLUMNS.filter(
            (col) => !TERMINAL_COLUMN_KEYS.has(col.key),
          ),
    [showCompleted],
  );

  // Configure PointerSensor to not activate on button clicks
  // Use distance constraint to prevent accidental drags on small movements
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Add full-width class to main content when component mounts
  useEffect(() => {
    const mainContent = document.querySelector(".main-content");
    if (mainContent) {
      mainContent.classList.add("pipeline-full-width");
    }

    return () => {
      if (mainContent) {
        mainContent.classList.remove("pipeline-full-width");
      }
    };
  }, []);

  // Handle drag start
  const handleDragStart = (event: DragStartEvent): void => {
    const { active } = event;
    setActiveId(String(active.id));
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;

    setActiveId(null);

    if (!over) {
      return;
    }

    const activeIdStr = String(active.id);
    const overId = String(over.id);

    if (activeIdStr === overId) {
      return;
    }

    // Find the active application
    const activeApplication = applications.find(
      (app) => app.id === Number(activeIdStr),
    );
    if (!activeApplication) {
      return;
    }

    // Find target column from drop zone
    const statusColPrefix = "status-col-";
    let targetColumn: KanbanColumn | undefined;

    if (overId.startsWith(statusColPrefix)) {
      // Dropped directly on a column
      const colKey = overId.replace(statusColPrefix, "");
      targetColumn = APPLICATION_KANBAN_COLUMNS.find(
        (col) => col.key === colKey,
      );
    } else {
      // Dropped on another application - find which column it's in
      const overApplication = applications.find(
        (app) => app.id === Number(overId),
      );
      if (overApplication) {
        targetColumn = APPLICATION_KANBAN_COLUMNS.find((col) =>
          col.statuses.includes(overApplication.status),
        );
      }
    }

    if (targetColumn) {
      // Don't move if already in this column
      if (targetColumn.statuses.includes(activeApplication.status)) {
        return;
      }
      // Pick the first status in the target column that is a valid transition
      const allowedTransitions =
        APPLICATION_VALID_TRANSITIONS[activeApplication.status] || [];
      // If the interview column has multiple valid transitions, prompt the user
      const validTargetStatuses = targetColumn.statuses.filter((s) =>
        allowedTransitions.includes(s),
      );
      if (targetColumn.key === "interview" && validTargetStatuses.length > 1) {
        setPendingInterviewChoice({ applicationId: activeApplication.id });
        return;
      }
      const targetStatus = validTargetStatuses[0];
      if (!targetStatus) {
        return; // No valid transition to this column
      }
      // Intercept rejected status — show rejection modal for reason
      if (targetStatus === "rejected") {
        setPendingRejection({
          applicationId: activeApplication.id,
          columnLabel: targetColumn.label,
        });
        return;
      }
      moveApplicationMutation.mutate({
        applicationId: activeApplication.id,
        status:
          targetStatus as import("../types/shared.types").ApplicationStatus,
        note: `Moved to ${targetColumn.label} via drag and drop`,
      });
    }
  };

  // Get applications for a Kanban column (column may contain multiple statuses)
  const getApplicationsForColumn = (
    column: KanbanColumn,
  ): PipelineApplication[] => {
    return applications.filter((app) => column.statuses.includes(app.status));
  };

  // Get active application for overlay
  const activeApplication = activeId
    ? applications.find((app) => app.id === Number(activeId))
    : undefined;

  // Navigation handlers
  const handleViewCandidate = (candidateId: number): void => {
    if (candidateId) {
      navigate(`/candidates/${candidateId}`);
    }
  };

  const handleViewJob = (jobId: number): void => {
    if (jobId) {
      navigate(`/jobs/${jobId}`);
    }
  };

  const handleTechCheck = (candidateId: number): void => {
    if (candidateId) {
      navigate(`/candidates/${candidateId}?tab=tech-check`);
    }
  };

  const handleStatusChange = useCallback(
    (
      applicationId: number,
      newStatus: import("../types/shared.types").ApplicationStatus,
    ): void => {
      moveApplicationMutation.mutate({
        applicationId,
        status: newStatus,
        note: `Status changed to ${newStatus} via badge dropdown`,
      });
    },
    [moveApplicationMutation],
  );

  // Define application columns for table view
  const applicationColumns = useMemo(
    () => [
      {
        id: "candidate_name",
        label: t("applications.table.candidateName"),
        render: (_value: unknown, row: PipelineApplication) => {
          const firstName = row.candidate_first_name || row.first_name || "";
          const lastName = row.candidate_last_name || row.last_name || "";
          return `${firstName} ${lastName}`.trim() || "-";
        },
      },
      {
        id: "job_title",
        label: t("applications.table.jobTitle"),
        render: (value: unknown) => <span>{(value as string) || "-"}</span>,
      },
      {
        id: "status",
        label: t("applications.table.status"),
        render: (value: unknown) => {
          const status = value as string;
          if (!status) return <span>-</span>;
          const config = APPLICATION_STATUS_CONFIG[status];
          return (
            <Chip
              label={config?.label || status}
              size="small"
              sx={{
                backgroundColor: config?.color || "#6b7280",
                color: "white",
                fontWeight: 500,
              }}
            />
          );
        },
      },
      {
        id: "expected_rate",
        label: t("applications.table.expectedRate"),
        render: (value: unknown) =>
          value ? `$${value}/${t("applications.day")}` : "-",
      },
      {
        id: "created_at",
        label: t("applications.table.appliedDate"),
        render: (value: unknown) =>
          value ? new Date(value as string).toLocaleDateString() : "-",
      },
      {
        id: "owner_name",
        label: t("applications.table.owner"),
        render: (value: unknown) => (value as string) || "-",
      },
    ],
    [t],
  );

  const handleApplicationRowClick = (row: PipelineApplication): void => {
    navigate(`/applications/${row.id}`);
  };

  if (applicationsLoading) {
    return (
      <div className="pipeline-loading">
        <div className="loading-spinner"></div>
        <p>{t("applications.loading")}</p>
      </div>
    );
  }

  if (applicationsError) {
    return (
      <div className="pipeline-error">
        <h2>{t("common:error")}</h2>
        <p>{(applicationsError as Error).message}</p>
        <button
          onClick={() => {
            queryClient.invalidateQueries("applications");
          }}
        >
          {t("common:retry")}
        </button>
      </div>
    );
  }

  return (
    <div className="pipeline-kanban">
      <div className="pipeline-header">
        <div className="pipeline-brand">
          <h1>{t("applications.title")}</h1>
          <p>{t("applications.trackApplications")}</p>
        </div>
        <div className="pipeline-header-actions">
          <div className="view-mode-switcher">
            <button
              className={`view-mode-btn ${viewMode === "kanban" ? "active" : ""}`}
              onClick={() => setViewMode("kanban")}
            >
              {t("applications.kanbanView")}
            </button>
            <button
              className={`view-mode-btn ${viewMode === "table" ? "active" : ""}`}
              onClick={() => setViewMode("table")}
            >
              {t("applications.tableView")}
            </button>
          </div>
          <button
            className={`show-completed-btn ${showCompleted ? "active" : ""}`}
            onClick={toggleShowCompleted}
            title={
              showCompleted
                ? t("applications.hideCompleted")
                : t("applications.showCompleted")
            }
          >
            <span className="completed-label">
              {showCompleted
                ? t("applications.hideCompleted")
                : t("applications.showCompleted")}
            </span>
            <span className="completed-counters">
              <span className="counter success">{terminalCounts.success}</span>
              <span className="counter rejected">
                {terminalCounts.rejected}
              </span>
            </span>
          </button>
          <button
            className="create-application-btn"
            onClick={() => setShowCreateApplicationModal(true)}
          >
            {t("applications.newApplication")}
          </button>
        </div>
      </div>

      {viewMode === "table" && (
        <TableView
          columns={applicationColumns}
          data={applications}
          loading={applicationsLoading}
          onRowClick={handleApplicationRowClick}
          searchable={true}
          filterable={true}
          selectable={false}
        />
      )}
      {viewMode !== "table" && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="pipeline-columns">
            {visibleColumns.map((column) => {
              const columnApps = getApplicationsForColumn(column);
              return (
                <StatusColumn
                  key={column.key}
                  column={column}
                  itemCount={columnApps.length}
                  highlighted={highlightedColumn === column.key}
                >
                  <SortableContext
                    items={columnApps.map((app) => app.id.toString())}
                    strategy={verticalListSortingStrategy}
                  >
                    {columnApps.map((application) => (
                      <SortableApplicationCard
                        key={application.id}
                        application={application}
                        onViewCandidate={handleViewCandidate}
                        onViewJob={handleViewJob}
                        onTechCheck={handleTechCheck}
                        onCardClick={(id) => navigate(`/applications/${id}`)}
                        onStatusChange={handleStatusChange}
                      />
                    ))}
                  </SortableContext>
                </StatusColumn>
              );
            })}
          </div>

          <DragOverlay>
            {activeApplication && (
              <DragOverlayCard application={activeApplication} />
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* Create Application Modal */}
      {showCreateApplicationModal && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <ApplicationForm
              onSave={() => setShowCreateApplicationModal(false)}
              onCancel={() => setShowCreateApplicationModal(false)}
            />
          </div>
        </div>
      )}

      {/* Interview Sub-Status Choice Dialog */}
      {pendingInterviewChoice && (
        <>
          <button
            className="modal-overlay"
            onClick={() => setPendingInterviewChoice(null)}
            aria-label="Close interview choice dialog"
          />
          <dialog className="modal interview-choice-dialog" open>
            <h3>
              {t("applications.interviewSubStatus", "Select Interview Status")}
            </h3>
            <p>
              {t(
                "applications.interviewSubStatusDesc",
                "Which interview status should this application move to?",
              )}
            </p>
            <div className="interview-choice-actions">
              {INTERVIEW_STATUSES.map((status) => (
                <button
                  key={status}
                  className={`interview-choice-btn interview-choice-${status.replace("interview_", "")}`}
                  onClick={() => {
                    moveApplicationMutation.mutate({
                      applicationId: pendingInterviewChoice.applicationId,
                      status,
                      note: `Moved to ${translateStatus(status, "applications.form.status", t)} via drag and drop`,
                    });
                    setPendingInterviewChoice(null);
                  }}
                  disabled={moveApplicationMutation.isLoading}
                >
                  {translateStatus(status, "applications.form.status", t)}
                </button>
              ))}
            </div>
          </dialog>
        </>
      )}

      {/* Rejection Popover for drag-to-rejected */}
      {pendingRejection && (
        <>
          <button
            className="modal-overlay"
            onClick={() => setPendingRejection(null)}
            aria-label="Close rejection dialog"
          />
          <dialog className="modal" open style={{ padding: "1rem" }}>
            <RejectionPopover
              show={true}
              onClose={() => setPendingRejection(null)}
              onReject={(reason, note) => {
                moveApplicationMutation.mutate({
                  applicationId: pendingRejection.applicationId,
                  status:
                    "rejected" as import("../types/shared.types").ApplicationStatus,
                  note: note || `Rejected via drag and drop`,
                  rejection_reason: reason,
                  rejection_reason_note: note,
                });
                setPendingRejection(null);
              }}
              isLoading={moveApplicationMutation.isLoading}
            />
          </dialog>
        </>
      )}
    </div>
  );
};

export default PipelineKanban;
